import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "@/components/ui/Spinner";
import { getContractorMegamap } from "../api";
import { monthLabel, projectPin, projectsPlural, SCOPE_LABEL } from "../components/mapStyle";
import type { MegamapProject, MegamapScopeEntry } from "../types";

/** Megamapa podwykonawcy: wszystkie projekty firmy (zlecone + planowane do zlecenia)
 *  jako piny na OSM + lista z licznikami. Klik → mapa projektu. Dane z wąskiego
 *  endpointu contractor-map (bez kwot i danych osobowych). */
export function ContractorMapPage() {
  const navigate = useNavigate();
  // Tryb podglądu dla wewnętrznych: /power-projects/contractor-preview/:previewContractorId
  const { previewContractorId } = useParams<{ previewContractorId?: string }>();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["contractor-map", "megamap", previewContractorId ?? "self"],
    queryFn: () => getContractorMegamap(previewContractorId),
  });
  const projects = data?.projects ?? [];
  const projectPath = (id: string) =>
    previewContractorId
      ? `/power-projects/contractor-preview/${previewContractorId}/${id}`
      : `/contractor/map/${id}`;

  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pinsRef = useRef<L.LayerGroup | null>(null);

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [52, 19],
      zoom: 6,
      attributionControl: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    pinsRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // Kontener istnieje tylko przy niepustej liście — refetch 0→N musi też
    // stworzyć mapę, stąd zależność od projects.length > 0 (audyt 2026-08-30).
  }, [isLoading, projects.length > 0]);

  useEffect(() => {
    const map = mapRef.current;
    const pins = pinsRef.current;
    if (!map || !pins) return;
    pins.clearLayers();
    const bounds: L.LatLngTuple[] = [];
    for (const project of projects) {
      if (project.lat == null || project.lng == null) continue;
      const state = project.scopes.some((s) => s.state === "ordered") ? "ordered" : "planned";
      const marker = L.marker([project.lat, project.lng], {
        icon: projectPin(state, project.name),
        keyboard: false,
      });
      marker.on("click", () => navigate(projectPath(project.id)));
      marker.addTo(pins);
      bounds.push([project.lat, project.lng]);
    }
    // Dopasowanie PO layoucie: fitBounds w tym samym ticku co montaż kontenera liczy
    // widok na złych wymiarach i mapa "ucieka" znad Polski.
    const timer = window.setTimeout(() => {
      map.invalidateSize();
      if (bounds.length > 0) map.fitBounds(L.latLngBounds(bounds).pad(0.25), { maxZoom: 11 });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [projects, navigate, previewContractorId]);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (isError) {
    return (
      <p className="p-6 text-sm text-[var(--color-primary)]">
        Nie udało się pobrać mapy projektów. Odśwież stronę lub spróbuj później.
      </p>
    );
  }

  return (
    // main layoutu ma własny padding i scroll — strona wypełnia go kolumną flex
    // (lg:h-full), więc na desktopie NIE MA pionowego scrolla strony: scrolluje się
    // wyłącznie lista projektów, mapa trzyma pełną wysokość.
    <div className="flex flex-col lg:h-full lg:min-h-0">
      {previewContractorId && (
        <div className="mb-3 border border-[var(--color-border)] border-l-4 border-l-[var(--color-primary)] bg-white px-3 py-2 text-sm dark:bg-gray-900">
          <span className="font-mono text-[11px] uppercase tracking-wider text-[var(--color-primary)]">
            Podgląd
          </span>{" "}
          — dokładnie to widzi firma <strong>{data?.previewName ?? "…"}</strong> na swojej
          mapie zamówień.
        </div>
      )}
      <div className="mb-4 flex items-center gap-2">
        <MapPin size={18} className="text-[var(--color-primary)]" />
        <h1 className="text-lg font-bold">Mapa zamówień</h1>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {projects.length === 0
            ? ""
            : `${projects.length} ${projectsPlural(projects.length)}`}
        </span>
      </div>

      {projects.length === 0 ? (
        <div className="border border-[var(--color-border)] bg-white p-8 text-center text-sm text-gray-500 dark:bg-gray-900 dark:text-gray-400">
          Nie masz jeszcze projektów na mapie. Projekt pojawi się tu wraz ze zleceniem
          (zgody WPW lub geodezja MDCP) albo gdy koordynator zapowie planowane zlecenie.
        </div>
      ) : (
        <div className="flex flex-col gap-4 lg:min-h-0 lg:flex-1 lg:flex-row lg:items-stretch">
          <div className="w-full flex-none space-y-2 lg:h-full lg:w-80 lg:overflow-y-auto lg:pr-1">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onOpen={() => navigate(projectPath(project.id))}
              />
            ))}
          </div>
          {/* `isolate` keeps Leaflet's z-indexes inside the map box, so the notifications
              panel and modals (z-50) paint above the map — same as MapTab. */}
          <div className="relative isolate z-0 h-[60vh] min-h-[20rem] flex-1 overflow-hidden border border-[var(--color-border)] lg:h-full">
            <div ref={containerRef} className="h-full w-full" />
          </div>
        </div>
      )}

      {projects.length > 0 && (
      <div className="mt-3 flex flex-none flex-wrap gap-4 text-[11px] text-gray-600 dark:text-gray-300">
        <span>
          <i className="mr-1 inline-block h-3 w-3 border-2 border-[#c8102e] bg-[#c8102e] align-[-1px]" />
          projekt zlecony
        </span>
        <span>
          <i className="mr-1 inline-block h-3 w-3 border-2 border-[#c8102e] bg-white align-[-1px]" />
          planowane do zlecenia (z terminem)
        </span>
      </div>
      )}
    </div>
  );
}

function scopeSummary(entry: MegamapScopeEntry): string {
  const c = entry.counters;
  if (entry.scope === "wpw") {
    const base = `${c.parcels_required ?? 0} działek · ${c.owners_total ?? 0} właścicieli`;
    return entry.state === "ordered" ? `${base} · ${c.owners_acquired ?? 0} pozyskanych` : base;
  }
  return `${c.parcels_in_route ?? 0} działek w zakresie · ${c.points_count ?? 0} punktów`;
}

function ProjectCard({ project, onOpen }: { project: MegamapProject; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      className="block w-full border border-[var(--color-border)] bg-white p-3 text-left transition-colors hover:border-[var(--color-primary)] dark:bg-gray-900"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold">{project.name}</span>
        <span className="flex flex-none flex-col items-end gap-1">
          {project.scopes.map((entry) => (
            <span
              key={entry.scope}
              className={
                entry.state === "ordered"
                  ? "bg-[var(--color-primary)] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-white"
                  : "border border-[var(--color-border)] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400"
              }
            >
              {entry.state === "ordered" ? "Zlecone" : "Planowane"}
            </span>
          ))}
        </span>
      </div>
      {!project.located && (
        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400">
          bez pinu — brak lokalizacji trasy
        </p>
      )}
      {(project.commune || project.county) && (
        <p className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
          {[project.commune && `gm. ${project.commune}`, project.county && `pow. ${project.county}`]
            .filter(Boolean)
            .join(", ")}
        </p>
      )}
      <div className="mt-1 space-y-0.5">
        {project.scopes.map((entry) => (
          <p key={entry.scope} className="text-[11px] text-gray-600 dark:text-gray-300">
            <span className="font-medium">{SCOPE_LABEL[entry.scope]}:</span> {scopeSummary(entry)}
            {entry.state === "planned" && entry.expected_month && (
              <span className="text-gray-500 dark:text-gray-400">
                {" "}
                · przewidywany termin: {monthLabel(entry.expected_month)}
              </span>
            )}
            {entry.state === "ordered" && entry.order_numbers.length > 0 && (
              <span className="text-gray-500 dark:text-gray-400">
                {" "}
                · {entry.order_numbers.join(", ")}
              </span>
            )}
          </p>
        ))}
      </div>
    </button>
  );
}
